import React, { useState } from 'react';
import { ExclamationTriangleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Modal } from './Modal';
import { Button } from './Button';
import { Checkbox } from './Checkbox';
import { cn } from '@/utils/cn';

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  /** When provided, displays the item name on a new line below the title (e.g. for delete confirmations). */
  itemName?: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'default';
  icon?: React.ReactNode;
  loading?: boolean;
  /** When true, shows a "Don't ask again" checkbox. When user confirms with it checked, onDontAskAgainChange(true) is called. */
  showDontAskAgain?: boolean;
  /** Label for the "Don't ask again" checkbox. */
  dontAskAgainLabel?: string;
  /** Called when user confirms with "Don't ask again" checked. Parent should set state to skip future confirmations for this page session. */
  onDontAskAgainChange?: (checked: boolean) => void;
}

/**
 * A polished confirmation dialog that replaces primitive window.confirm().
 * Uses the app's design system and supports destructive (danger) and warning variants.
 * Supports "Don't ask again" for bulk delete flows on the same page.
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  itemName,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  icon,
  loading = false,
  showDontAskAgain = false,
  dontAskAgainLabel = "Don't ask again for this session",
  onDontAskAgainChange,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dontAskAgainChecked, setDontAskAgainChecked] = useState(false);
  const busy = loading || isSubmitting;

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      if (showDontAskAgain && dontAskAgainChecked && onDontAskAgainChange) {
        onDontAskAgainChange(true);
      }
      await onConfirm();
      onClose();
    } catch {
      // Let parent handle error display; keep dialog open so user can retry or cancel
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setDontAskAgainChecked(false);
    onClose();
  };

  const iconBgClass =
    variant === 'danger'
      ? 'bg-red-100 dark:bg-red-900/30'
      : variant === 'warning'
        ? 'bg-amber-100 dark:bg-amber-900/30'
        : 'bg-[var(--color-secondary-bg)]';

  const iconColorClass =
    variant === 'danger'
      ? 'text-red-600 dark:text-red-400'
      : variant === 'warning'
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-[var(--color-secondary-text)]';

  const defaultIcon =
    variant === 'danger' ? (
      <TrashIcon className={cn('w-6 h-6', iconColorClass)} />
    ) : (
      <ExclamationTriangleIcon className={cn('w-6 h-6', iconColorClass)} />
    );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="sm">
      <div className="flex gap-4">
        <div
          className={cn(
            'flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center',
            iconBgClass
          )}
        >
          {icon !== undefined ? icon : defaultIcon}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-[var(--color-surface-text)]">
            {title}
          </h3>
          {itemName && (
            <p className="mt-2 text-base font-medium text-[var(--color-surface-text)] break-words">
              {itemName}
            </p>
          )}
          {description && (
            <p className="mt-2 text-sm text-[var(--color-secondary-text)]">
              {description}
            </p>
          )}
          {showDontAskAgain && (
            <label className="mt-4 flex items-center gap-2 cursor-pointer select-none">
              <Checkbox
                checked={dontAskAgainChecked}
                onChange={setDontAskAgainChecked}
                disabled={busy}
              />
              <span className="text-sm text-[var(--color-secondary-text)]">
                {dontAskAgainLabel}
              </span>
            </label>
          )}
          <div className="mt-6 flex justify-end gap-3">
            <Button variant="outline" onClick={handleClose} disabled={busy}>
              {cancelLabel}
            </Button>
            <Button
              variant={variant === 'danger' || variant === 'warning' ? 'danger' : 'primary'}
              onClick={handleConfirm}
              disabled={busy}
              loading={busy}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
